import Keycloak from 'keycloak-js'
import { runtimeConfig } from '../runtimeConfig'

/*
  Constructed on first use, not at module load: the realm and client id arrive from
  /config.json at startup, and a Keycloak built while this module is being imported
  would be built from the build-time fallbacks.
*/
let instance: Keycloak | null = null

/** The Keycloak client. Authorization code + PKCE (S256), no client secret in the browser. */
export function getKeycloak(): Keycloak {
  if (instance === null) {
    const { keycloak: config } = runtimeConfig()
    instance = new Keycloak({ url: config.url, realm: config.realm, clientId: config.clientId })
  }
  return instance
}

/**
 * True when Keycloak is configured. With no server URL the SPA runs in STUB MODE: it
 * sends no token and relies on the API's development stub. A function, not a
 * constant, because the answer is not known until the runtime configuration loads.
 */
export function isKeycloakConfigured(): boolean {
  return Boolean(runtimeConfig().keycloak.url)
}

/** Parsed access-token claims we care about. */
export interface TokenClaims {
  sub?: string
  preferred_username?: string
  email?: string
  name?: string
  realm_access?: { roles?: string[] }
}

let refreshTimer: ReturnType<typeof setInterval> | undefined

/**
 * Authenticates before the app renders. keycloak-js redirects to the login page when
 * there is no session, so an unauthenticated user never reaches React at all. Returns
 * false in stub mode.
 */
export async function initKeycloak(): Promise<boolean> {
  if (!isKeycloakConfigured()) {
    return false
  }

  const authenticated = await getKeycloak().init({
    onLoad: 'login-required',
    pkceMethod: 'S256',
    // The CSP forbids frames (frame-ancestors 'none' on us; Keycloak sets its own), so
    // the status iframe cannot work. The refresh timer below covers session expiry.
    checkLoginIframe: false,
  })

  if (!authenticated) {
    throw new Error('Keycloak did not authenticate the session.')
  }

  refreshTimer = setInterval(() => {
    void getKeycloak()
      .updateToken(70)
      .catch(() => {
        // A failed refresh means the session is gone. Back to the login page rather
        // than leaving the SPA making 401 calls it cannot recover from.
        void getKeycloak().login()
      })
  }, 60_000)

  return true
}

export function stopTokenRefresh(): void {
  if (refreshTimer) {
    clearInterval(refreshTimer)
    refreshTimer = undefined
  }
}

/** A token guaranteed fresh for at least `minValiditySeconds`, or undefined in stub mode. */
export async function getFreshToken(minValiditySeconds = 30): Promise<string | undefined> {
  if (!isKeycloakConfigured()) {
    return undefined
  }

  try {
    await getKeycloak().updateToken(minValiditySeconds)
  } catch {
    void getKeycloak().login()
    return undefined
  }

  return getKeycloak().token
}

const STEP_UP_KEY = 'dataroom.stepUpAt'

/**
 * Sends the browser back to Keycloak for the second factor (ADR 0011): `acr` `mfa`,
 * essential, so Keycloak runs the flow's step-up level and fails rather than answer with
 * less. Over the SSO cookie only the code is asked for, and the user comes back to the
 * page they were on. Returns false, without redirecting, when a step-up was already
 * tried in the last minute: the API asking again straight after one means something is
 * wrong, and bouncing between the two would hide it.
 */
export function stepUp(): boolean {
  if (!isKeycloakConfigured()) return false
  try {
    const last = Number(sessionStorage.getItem(STEP_UP_KEY) ?? 0)
    if (Date.now() - last < 60_000) return false
    sessionStorage.setItem(STEP_UP_KEY, String(Date.now()))
  } catch {
    // Storage blocked: redirect anyway; Keycloak's own errors still stop a loop.
  }
  void getKeycloak().login({
    acr: { values: [STEP_UP_ACR], essential: true },
    redirectUri: window.location.href,
  })
  return true
}

/** The level the realm calls a second factor (acr.loa.map). */
export const STEP_UP_ACR = 'mfa'

export function logout(): void {
  stopTokenRefresh()
  if (isKeycloakConfigured()) {
    void getKeycloak().logout({ redirectUri: window.location.origin })
  }
}

export function claims(): TokenClaims {
  if (!isKeycloakConfigured()) {
    return {}
  }
  return (getKeycloak().tokenParsed ?? {}) as TokenClaims
}
